import { useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { useIntl } from "react-intl"

const getStatusColor = (status) => {
    if (status >= 500) return "bg-red-100 text-red-800"
    if (status >= 400) return "bg-yellow-100 text-yellow-800"
    if (status >= 200 && status < 300) return "bg-green-100 text-green-800"
    return "bg-gray-100 text-gray-800"
}

const JsonViewer = ({ data }) => (
    <pre className="text-xs bg-gray-800 text-white p-2 rounded-md overflow-x-auto">
        {JSON.stringify(data, null, 2)}
    </pre>
)

export default function HttpLogItem({ log }) {

    const intl = useIntl();

    const [isOpen, setIsOpen] = useState(false)
    const status = log.responseStatus || 0
    const isError = status >= 400 || !!log.errorMessage

    return (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white min-w-[500px]">
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
                    <div className="flex items-center gap-2">
                        {isError && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white">
                                {intl.formatMessage({ id: "dga.logs.error", defaultMessage: "Error" })}
                            </span>
                        )}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                            {status.toFixed(0)}
                        </span>
                        <p className="text-sm text-gray-900 font-mono truncate">{log.endpointPath}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500">
                            {intl.formatDate(new Date(log.timestamp), {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                                second: "numeric",
                            })}
                        </p>
                        <button className="text-gray-400 hover:text-gray-600">
                            {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {isOpen && (
                    <div className="mt-4 space-y-4">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-700">{intl.formatMessage({ id: "dga.logs.request", defaultMessage: "Request" })}</h4>
                            <div className="mt-1 space-y-2">
                                <p className="text-xs text-gray-500">{intl.formatMessage({ id: "dga.logs.url", defaultMessage: "URL" })}: {log.requestUrl}</p>
                                <p className="text-xs text-gray-500">{intl.formatMessage({ id: "dga.logs.duration", defaultMessage: "Duration" })}: {log.durationMs?.toFixed(2)}ms</p>
                                <div>
                                    <h5 className="text-xs font-medium text-gray-600">{intl.formatMessage({ id: "dga.logs.headers", defaultMessage: "Headers" })}</h5>
                                    <JsonViewer data={log.requestHeaders} />
                                </div>
                                <div>
                                    <h5 className="text-xs font-medium text-gray-600">{intl.formatMessage({ id: "dga.logs.payload", defaultMessage: "Payload" })}</h5>
                                    <JsonViewer data={log.requestPayload} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-sm font-semibold text-gray-700">{intl.formatMessage({ id: "dga.logs.response", defaultMessage: "Response" })}</h4>
                            <div className="mt-1 space-y-2">
                                {log.errorMessage && <p className="text-xs text-red-500">{intl.formatMessage({ id: "dga.logs.error", defaultMessage: "Error" })}: {log.errorMessage}</p>}
                                <div>
                                    <h5 className="text-xs font-medium text-gray-600">{intl.formatMessage({ id: "dga.logs.headers", defaultMessage: "Headers" })}</h5>
                                    <JsonViewer data={log.responseHeaders} />
                                </div>
                                <div>
                                    <h5 className="text-xs font-medium text-gray-600">{intl.formatMessage({ id: "dga.logs.body", defaultMessage: "Body" })}</h5>
                                    <JsonViewer data={log.responseBody} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
